import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";
import { leadScoringApi } from "./leadScoring.api";

// Every comm-log write touches a client's engagement timeline, which is
// exactly what lead scoring is computed from server-side. Without this
// the LeadScoring leaderboard + dashboard HotLeadsWidget would keep
// serving stale numbers until a manual refresh, even though the score
// changed the moment the call/text/email/note landed.
const invalidateLeadScoringCaches = (dispatch) => {
  try {
    dispatch(leadScoringApi.util.invalidateTags(["LeadScores", "HotLeads", "ScoreStats"]));
  } catch {
    // Non-fatal — if the leadScoring slice isn't mounted in this app,
    // we just skip. Shouldn't happen in practice but cheap to guard.
  }
};

export const communicationLogApi = createApi({
  reducerPath: "communicationLogApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["CommunicationLogs", "CommunicationStats", "UnifiedComms"],
  endpoints: (builder) => ({
    // NEW: Get unified communications (SMS + Email + Manual Logs)
    getUnifiedCommunications: builder.query({
      query: (params = {}) => ({
        url: "/communication-logs/unified",
        method: "GET",
        params,
      }),
      providesTags: ["UnifiedComms"],
      transformResponse: (response) => response.data || [],
    }),

    // NEW: Get unified stats
    getUnifiedCommunicationStats: builder.query({
      query: () => ({
        url: "/communication-logs/unified/stats",
        method: "GET",
      }),
      providesTags: ["CommunicationStats"],
      transformResponse: (response) => response.data || {
        total: 0,
        calls: 0,
        texts: 0,
        emails: 0,
        meetings: 0,
        unread: 0,
      },
    }),

    // Get all communication logs
    getCommunicationLogs: builder.query({
      query: (params = {}) => ({
        url: "/communication-logs",
        method: "GET",
        params,
      }),
      providesTags: ["CommunicationLogs"],
      transformResponse: (response) => response.data || [],
    }),

    // Get communication statistics
    getCommunicationStats: builder.query({
      query: () => ({
        url: "/communication-logs/stats",
        method: "GET",
      }),
      providesTags: ["CommunicationStats"],
      transformResponse: (response) => response.data || {
        total: 0,
        calls: 0,
        texts: 0,
        emails: 0,
        meetings: 0,
        unread: 0,
      },
    }),

    // Get logs by type
    getCommunicationLogsByType: builder.query({
      query: (type) => ({
        url: `/communication-logs/type/${type}`,
        method: "GET",
      }),
      providesTags: (result, error, type) => [
        { type: "CommunicationLogs", id: type },
      ],
      transformResponse: (response) => response.data || [],
    }),

    // Get logs by client
    getCommunicationLogsByClient: builder.query({
      query: (clientId) => ({
        url: `/communication-logs/client/${clientId}`,
        method: "GET",
      }),
      providesTags: (result, error, clientId) => [
        { type: "CommunicationLogs", id: `client-${clientId}` },
      ],
      transformResponse: (response) => response.data || [],
    }),

    // Get follow-up logs
    getFollowUpLogs: builder.query({
      query: () => ({
        url: "/communication-logs/follow-ups",
        method: "GET",
      }),
      providesTags: [{ type: "CommunicationLogs", id: "follow-ups" }],
      transformResponse: (response) => response.data || [],
    }),

    // Search communication logs
    searchCommunicationLogs: builder.query({
      query: (searchQuery) => ({
        url: "/communication-logs/search",
        method: "GET",
        params: { query: searchQuery },
      }),
      providesTags: ["CommunicationLogs"],
      transformResponse: (response) => response.data || [],
    }),

    // Get logs by date range
    getCommunicationLogsByDateRange: builder.query({
      query: ({ startDate, endDate }) => ({
        url: "/communication-logs/date-range",
        method: "GET",
        params: { startDate, endDate },
      }),
      providesTags: ["CommunicationLogs"],
      transformResponse: (response) => response.data || [],
    }),

    // Get single log by ID
    getCommunicationLogById: builder.query({
      query: (id) => ({
        url: `/communication-logs/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [
        { type: "CommunicationLogs", id },
      ],
      transformResponse: (response) => response.data,
    }),

    // Create new communication log
    createCommunicationLog: builder.mutation({
      query: (logData) => ({
        url: "/communication-logs",
        method: "POST",
        body: logData,
      }),
      invalidatesTags: ["CommunicationLogs", "CommunicationStats", "UnifiedComms"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try { await queryFulfilled; invalidateLeadScoringCaches(dispatch); } catch {}
      },
    }),

    // Update communication log
    updateCommunicationLog: builder.mutation({
      query: ({ id, ...logData }) => ({
        url: `/communication-logs/${id}`,
        method: "PUT",
        body: logData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "CommunicationLogs", id },
        "CommunicationLogs",
        "CommunicationStats",
        "UnifiedComms",
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try { await queryFulfilled; invalidateLeadScoringCaches(dispatch); } catch {}
      },
    }),

    // Delete communication log
    deleteCommunicationLog: builder.mutation({
      query: (id) => ({
        url: `/communication-logs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CommunicationLogs", "CommunicationStats", "UnifiedComms"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try { await queryFulfilled; invalidateLeadScoringCaches(dispatch); } catch {}
      },
    }),

    // Mark as read — server-side this counts as "replied to / engaged
    // with", which feeds the score, so refresh the leaderboard too.
    markAsRead: builder.mutation({
      query: (id) => ({
        url: `/communication-logs/${id}/read`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "CommunicationLogs", id },
        "CommunicationStats",
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try { await queryFulfilled; invalidateLeadScoringCaches(dispatch); } catch {}
      },
    }),

    // Toggle star
    toggleStar: builder.mutation({
      query: (id) => ({
        url: `/communication-logs/${id}/star`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "CommunicationLogs", id },
      ],
    }),
  }),
});

export const {
  useGetUnifiedCommunicationsQuery,
  useGetUnifiedCommunicationStatsQuery,
  useGetCommunicationLogsQuery,
  useGetCommunicationStatsQuery,
  useGetCommunicationLogsByTypeQuery,
  useGetCommunicationLogsByClientQuery,
  useGetFollowUpLogsQuery,
  useSearchCommunicationLogsQuery,
  useGetCommunicationLogsByDateRangeQuery,
  useGetCommunicationLogByIdQuery,
  useCreateCommunicationLogMutation,
  useUpdateCommunicationLogMutation,
  useDeleteCommunicationLogMutation,
  useMarkAsReadMutation,
  useToggleStarMutation,
} = communicationLogApi;
